import { ManageHistoryOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Card,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import moment from "moment";
import React, { useEffect, useState } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { formatPrice } from "../../../shared/functions/formatPrice";
import { handleError } from "../../../shared/functions/handleError";
import { usePagination } from "../../../shared/hooks/usePagination";
import { IClientAction } from "../../../shared/interfaces/client/IClient";
import { IModule } from "../../../shared/interfaces/module/IModule";
import { RootState } from "../../../store/store";
import i18n from "../../../utils/localization/i18n";
import { ModuleService } from "../../module/service";

interface IProps {
  control: Control<IClientAction>;
  watch: UseFormWatch<IClientAction>;
  errors: FieldErrors<IClientAction>;
  isUpdate?: boolean;
}

const ClientForm = ({ control, watch, isUpdate = false, errors }: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { country, clientStatus, teamSize } = useSelector(
    (state: RootState) => state.enums,
  );
  const [modules, setModules] = useState<IModule[]>([]);
  const { pagination } = usePagination({ perPage: 30 });
  const selectedModules = modules?.filter((module) =>
    watch("modules")?.some((sModule) => sModule.moduleId === module?.id),
  );
  useEffect(() => {
    ModuleService.get({ ...pagination, page: pagination?.page + 1 })
      .then((response) => {
        setModules(response?.data);
      })
      .catch(handleError);
  }, [pagination]);

  return (
    <Card>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 3 }}>
          <Controller
            name={"name"}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                error={!!error?.message}
                helperText={error?.message ? t(error?.message) : ""}
                value={value ?? ""}
                onChange={onChange}
                label={t("client.name")}
                sx={{ width: "100%" }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 3 }}>
          <Controller
            name={"email"}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                error={!!error?.message}
                helperText={error?.message ? t(error?.message) : ""}
                value={value ?? ""}
                onChange={onChange}
                type={"email"}
                label={t("general.email")}
                sx={{ width: "100%" }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Controller
            name={"status"}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl fullWidth>
                <InputLabel
                  className={!!error?.message?.length ? "Mui-error" : ""}
                  id="status-label"
                >
                  {t("general.status")}
                </InputLabel>
                <Select
                  error={!!error?.message}
                  labelId="status-label"
                  id="status-select"
                  value={value ?? ""}
                  label={t("general.status")}
                  onChange={onChange}
                >
                  {clientStatus?.map((status) => {
                    return (
                      <MenuItem key={status} value={status}>
                        {t(`client.status.${status}`)}
                      </MenuItem>
                    );
                  })}
                </Select>
                {!!error?.message?.length && (
                  <FormHelperText>{t(error?.message)}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Controller
            name={"teamSize"}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl fullWidth>
                <InputLabel
                  className={!!error?.message?.length ? "Mui-error" : ""}
                  id="teamSize-label"
                >
                  {t("general.teamSize")}
                </InputLabel>
                <Select
                  error={!!error?.message}
                  labelId="teamSize-label"
                  id="teamSize-select"
                  value={value ?? ""}
                  label={t("general.teamSize")}
                  onChange={onChange}
                >
                  {teamSize?.map((size) => {
                    return (
                      <MenuItem key={size} value={size}>
                        {t(`client.teamSize.${size}`)}
                      </MenuItem>
                    );
                  })}
                </Select>
                {!!error?.message?.length && (
                  <FormHelperText>{t(error?.message)}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Controller
            name="apiKey"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label={t("general.apiKey")}
                value={value ?? ""}
                onChange={onChange}
                error={!!error?.message}
                helperText={error?.message ? t(error.message) : ""}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ mr: "10px" }}>
                      <IconButton
                        onClick={() => onChange(uuidv4())}
                        edge="end"
                        title={t("general.generate")}
                      >
                        <ManageHistoryOutlined />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 3 }}>
          <Controller
            name={"address"}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                error={!!error?.message}
                helperText={error?.message ? t(error?.message) : ""}
                value={value ?? ""}
                onChange={onChange}
                label={t("general.address")}
                sx={{ width: "100%" }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 3 }}>
          <Controller
            name={"phone"}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                error={!!error?.message}
                helperText={error?.message ? t(error?.message) : ""}
                value={value ?? ""}
                onChange={onChange}
                type={"tel"}
                label={t("general.phone")}
                sx={{ width: "100%" }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 3 }}>
          <Controller
            name={"country"}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl fullWidth>
                <InputLabel
                  className={!!error?.message?.length ? "Mui-error" : ""}
                  id="country-label"
                >
                  {t("general.country")}
                </InputLabel>
                <Select
                  error={!!error?.message}
                  labelId="country-label"
                  id="country-select"
                  value={value ?? ""}
                  label={t("general.country")}
                  onChange={onChange}
                >
                  {country?.map((country) => {
                    return (
                      <MenuItem key={country} value={country}>
                        {t(`country.${country}`)}
                      </MenuItem>
                    );
                  })}
                </Select>
                {!!error?.message?.length && (
                  <FormHelperText>{t(error?.message)}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 3 }}>
          <Controller
            name={"expirationDate"}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth sx={{ width: "100%" }}>
                <LocalizationProvider
                  dateAdapter={AdapterMoment}
                  adapterLocale={i18n.language}
                >
                  <DemoContainer components={["DatePicker"]}>
                    <DatePicker
                      sx={{ width: "100%" }}
                      label={t("general.expirationDate")}
                      format="DD-MM-YYYY"
                      value={field.value ? moment(field.value) : undefined}
                      onChange={(value) =>
                        field.onChange(moment(value)?.toISOString())
                      }
                      slotProps={{
                        textField: {
                          error: !!error?.message?.length,
                          helperText: t(error?.message as string),
                        },
                      }}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Controller
            name={"mb"}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                error={!!error?.message}
                helperText={error?.message ? t(error?.message) : ""}
                value={value ?? ""}
                onChange={onChange}
                label={t("general.mb")}
                sx={{ width: "100%" }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Controller
            name={"pib"}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                error={!!error?.message}
                helperText={error?.message ? t(error?.message) : ""}
                value={value ?? ""}
                onChange={onChange}
                label={t("general.pib")}
                sx={{ width: "100%" }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Controller
            name={"contactPerson"}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                error={!!error?.message}
                helperText={error?.message ? t(error?.message) : ""}
                value={value ?? ""}
                onChange={onChange}
                label={t("general.contactPerson")}
                sx={{ width: "100%" }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Controller
            name={"notificationEmails"}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Autocomplete
                multiple
                freeSolo
                options={[] as string[]}
                value={value ?? []}
                onChange={(_, emails) =>
                  onChange(
                    Array.from(
                      new Set(
                        emails.map((email) => email.trim()).filter(Boolean),
                      ),
                    ),
                  )
                }
                renderTags={(emails, getTagProps) =>
                  emails.map((email, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={email}
                      label={email}
                      size="small"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    type="email"
                    label={t("client.notificationEmails")}
                    error={!!error}
                    helperText={t(
                      error
                        ? "validation.email"
                        : "client.notificationEmailsHelper",
                    )}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="description"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label={t("general.description")}
                value={value ?? ""}
                onChange={onChange}
                error={!!error?.message}
                helperText={t(error?.message as string)}
                multiline
                minRows={3}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="modules"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl fullWidth error={!!error?.message}>
                <InputLabel id="modules-label">
                  {t("general.modules")}
                </InputLabel>
                <Select
                  labelId="modules-label"
                  multiple
                  value={(value ?? []).map((v) => v.moduleId)}
                  onChange={(event) => {
                    const selectedIds = event.target.value as number[];
                    onChange(selectedIds.map((id) => ({ moduleId: id })));
                  }}
                  renderValue={(selected) =>
                    (selected as number[])
                      .map(
                        (id) =>
                          t(
                            `modules.${modules.find((mod) => mod.id === id)?.name}`,
                          ) ?? `ID ${id}`,
                      )
                      .join(", ")
                  }
                >
                  {modules.map((module) => (
                    <MenuItem key={module.id} value={module.id}>
                      {t(`modules.${module.name}`)} -{" "}
                      {formatPrice(module.price)}
                    </MenuItem>
                  ))}
                </Select>
                {!!error?.message && (
                  <FormHelperText>{t(error.message)}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        {selectedModules?.map((module) => (
          <Grid size={{ xs: 12, md: 6, lg: 3 }} key={module.id}>
            <Card>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  mb: "10px",
                }}
              >
                <Box>
                  <Typography variant={"body1"} sx={{ fontWeight: "bold" }}>
                    {t(`modules.${module.name}`)}
                  </Typography>
                  <Typography variant={"body2"}>
                    {module.description}
                  </Typography>
                </Box>
                <Typography variant={"body1"} sx={{ fontWeight: "bold" }}>
                  {formatPrice(module.price)}
                </Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant={"contained"}
          color={"secondary"}
          sx={{ marginTop: "20px" }}
          type={"button"}
          onClick={() => navigate(-1)}
        >
          {t("button.cancel")}
        </Button>
        <Button
          variant={"contained"}
          sx={{ margin: "20px 0 0 10px" }}
          type={"submit"}
        >
          {t(isUpdate ? "button.update" : "button.create")}
        </Button>
      </Box>
    </Card>
  );
};

export default ClientForm;
